/**
 * progressions.js provides functions for calculating progression data based on
 * expectations, milestones, levels and student progress.
 */

import EmberObject from '@ember/object';
import { isEmpty } from '@ember/utils';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import MilestoneType from '@linc-technologies/linced-common/enums/milestone-type';
import moment from 'moment';
import type {
  Expectation,
  ExpectationIndicesData,
  Level,
  Milestone,
  Progression,
  ProgressionData,
  ProgressionDataPoint,
  ProgressionExpectations,
  ProgressionObject,
  ProgressionPerson,
  ProgressionSubject,
  ProgressMarker,
  SubjectProgressions,
} from '@linc-technologies/linced-common/utils/typedefs';

// present is `!isEmpty(value)` as a type guard. Don't trust its false branch:
// it narrows to null | undefined, but an empty array is also "not present".
function present<T>(value: T | null | undefined): value is T {
  return !isEmpty(value);
}

export const progressColors = [
  '#e57373', // red 300
  '#ff9800', // orange 500
  '#6f94eb', // linced-blue a200
  '#4caf50', // green 500
  '#ffd600', // yellow a700
];

export const progressChartColors = [
  '#FF9800', // orange 500
  '#6f94eb', // linced-blue a200
  '#4caf50', // green 500
  'transparent',
];

/**
 * calculateYearLevelMilestoneDate calculates the actual date a milestone falls on,
 * considering calendar year, milestone date, and rollover date
 *
 * @param {number} year - The calendar year to consider.
 * @param {Object} milestone - The Milestone model.
 * @param {String} rolloverMonthDate - The rollover date for the school in MM-DD format.
 * @returns {moment}
 */
export function calculateYearLevelMilestoneDate(
  year: number,
  milestone: Milestone,
  rolloverMonthDate: string,
): moment.Moment {
  // TS-NARROWED: isEmpty also accepts null and undefined. No caller sends them.
  if (isEmpty(rolloverMonthDate)) {
    rolloverMonthDate = '01-01';
  }
  // "Now" of the year we're concerned with, otherwise the below calculations make no sense
  const now = moment().year(year);
  const milestoneDate = moment(
    `${year}-${('0' + milestone.month).slice(-2)}-${('0' + milestone.day).slice(
      -2,
    )}`,
  );
  const rolloverDate = moment(`${year}-${rolloverMonthDate}`);
  // If milestone is before rollover and now is after rollover add 1 year to milestone
  if (
    milestoneDate.isBefore(rolloverDate, 'day') &&
    now.isSameOrAfter(rolloverDate, 'day')
  ) {
    milestoneDate.add(1, 'year');
  }
  // If milestone is after rollover and now is before rollover subtract 1 year from milestone
  if (
    milestoneDate.isSameOrAfter(rolloverDate, 'day') &&
    now.isBefore(rolloverDate, 'day')
  ) {
    milestoneDate.subtract(1, 'year');
  }

  return milestoneDate;
}

/**
 * isYearLevelMilestoneCurrent returns whether the provided milestone is the
 * current milestone based on current year level.
 *
 * @param {Object} milestone - The Milestone model.
 * @param {number} currentYearLevel - The current year level of the student.
 * @param {Object} nextMilestone - The Milestone model for the following milestone.
 * @param {String} rolloverMonthDate - The rollover date for the school in MM-DD format.
 * @param {String} asOnDate - Specify a date for "current".
 * @returns {boolean}
 */
function isYearLevelMilestoneCurrent(
  milestone: Milestone,
  currentYearLevel: number,
  nextMilestone: Milestone | undefined,
  rolloverMonthDate: string,
  asOnDate: string | undefined,
): boolean {
  if (currentYearLevel > milestone.get('yearLevel')) {
    return false;
  }

  let now = moment();
  if (present(asOnDate)) {
    now = moment(asOnDate);
  }
  // TS-NARROWED: getCurrentMilestone always sends a non-empty string.
  // This fallback cannot occur.
  if (isEmpty(rolloverMonthDate)) {
    rolloverMonthDate = '01-01';
  }

  const year = now.year();
  const milestoneDate = calculateYearLevelMilestoneDate(
    year,
    milestone,
    rolloverMonthDate,
  );

  const isLastMilestoneForYearLevel =
    !present(nextMilestone) ||
    milestone.get('yearLevel') !== nextMilestone.get('yearLevel');

  return now.isSameOrBefore(milestoneDate) || isLastMilestoneForYearLevel;
}

/**
 * getProgressions returns the current expectations.
 *
 * @param entity
 * @param {Level[]} levels - An array of associated Levels.
 * @param {Expectation} expectation - An expectation model.
 * @param {Progression} progression - A progression model to generate current
 *     expectations for.
 * @returns {ProgressionExpectations}
 */
export function getProgressions(
  entity: ProgressionSubject,
  levels: readonly Level[],
  expectation: Expectation | undefined,
  progression: Progression | undefined,
): ProgressionObject<ProgressionExpectations> | undefined {
  if (isEmpty(levels)) {
    // If no levels are defined for this subject/page, there won't be any data / we can't calculate anything.
    return;
  }

  const data = EmberObject.create({});
  if (present(expectation)) {
    data.set('expected', levels[(expectation.get('atIndex') ?? 0) + 1]);
  }

  if (present(progression)) {
    data.set('current', progression.get('level'));
    data.set('judgement', progression.get('judgement'));
  }

  return data;
}

/**
 * subjectProgressions returns subject specific progression summary data.
 *
 * @param {Subject} subject - The subject to generate progression data for.
 * @param {Level[]} levels - The levels to generate progression data for in
 *     sequential order.
 * @param {Expectation} expectation - The expectation to genrerate progression
 *     data for.
 * @param {Progression} progression - The progression to genrerate progression
 *     data for.
 * @returns {SubjectProgressions}
 */
export function subjectProgressions(
  subject: ProgressionSubject,
  levels: readonly Level[],
  expectation: Expectation | undefined,
  progression: Progression | undefined,
): ProgressionObject<SubjectProgressions> | undefined {
  if (isEmpty(levels)) {
    // If no levels are defined for this subject, there won't be any data / we can't calculate anything.
    return;
  }

  let showLevels: Level[] = [];
  // levels is non-empty (checked above), so getProgressions returns data.
  const subjectData = getProgressions(
    subject,
    levels,
    expectation,
    progression,
  ) as ProgressionObject<SubjectProgressions>;
  subjectData.set('subject_id', subject.id);

  if (present(expectation) && present(progression)) {
    const index = levels.findIndex(
      (level) => level.id === progression.get('level.id'),
    );
    const firstLevel = Math.max(0, Math.min(index - 1, levels.length - 3));
    const lastLevel = firstLevel + 3;
    showLevels = levels.slice(firstLevel, lastLevel);
  } else if (present(expectation)) {
    const index = expectation.get('atIndex') ?? 0;
    const firstLevel = Math.max(0, Math.min(index - 1, levels.length - 3));
    const lastLevel = firstLevel + 3;
    showLevels = levels.slice(firstLevel, lastLevel);
  } else {
    showLevels = levels.slice(0, 3);
  }

  subjectData.set('showLevels', showLevels);

  return subjectData;
}

export function getCurrentMilestone(
  milestones: readonly Milestone[],
  person: ProgressionPerson,
  rolloverMonthDate: string,
  asOnDate?: string,
): Milestone | undefined {
  const lastIndex = milestones.length - 1;
  let { weeksAtSchool, monthsAtSchool } = person;
  // A learner with no year level ('') compares as year 0.
  let numericCurrentYearLevel = Number(person.numericCurrentYearLevel);
  if (present(asOnDate)) {
    // The methods return undefined with no first schooling date, or before
    // it. NaN keeps that result: it matches no school time milestone.
    weeksAtSchool = person.weeksAtSchoolOnDate(asOnDate) ?? NaN;
    monthsAtSchool = person.monthsAtSchoolOnDate(asOnDate) ?? NaN;
    numericCurrentYearLevel = person.yearLevelOnDate(asOnDate);
  }
  // TS-NARROWED: isEmpty also accepts null and undefined. No caller sends them.
  if (isEmpty(rolloverMonthDate)) {
    rolloverMonthDate = '01-01';
  }
  return milestones.find((milestone, index) => {
    if (index === lastIndex) {
      // This is the last milestone in the list.  Go ahead and return it.
      // The student can't be beyond this milestone, but we should return _a_ milestone.
      return true;
    }
    switch (milestone.get('type')) {
      case MilestoneType.WEEKS_AT_SCHOOL:
        if (weeksAtSchool <= milestone.get('duration')) {
          return true;
        }
        break;
      case MilestoneType.MONTHS_AT_SCHOOL:
        if (monthsAtSchool <= milestone.get('duration')) {
          return true;
        }
        break;
      case MilestoneType.YEAR_LEVEL:
        if (
          isYearLevelMilestoneCurrent(
            milestone,
            numericCurrentYearLevel,
            milestones[index + 1],
            rolloverMonthDate,
            asOnDate,
          )
        ) {
          return true;
        }
        break;
    }
  });
}

/**
 * @deprecated use generate data functions below instead to ensure raw data is
 *     used when sending to the server.
 */
export function getProgressionChartData(
  expectation: Expectation | undefined,
  milestone: Milestone,
  levels: readonly Level[],
  progression: Progression | undefined,
): { expectation?: ProgressionDataPoint; progress?: ProgressMarker } {
  const result: {
    expectation?: ProgressionDataPoint;
    progress?: ProgressMarker;
  } = {};

  // Defaults in case there is no expectation for this Milestone
  let indices = {
    belowIndex: 0,
    atIndex: 0,
    aboveIndex: 0,
    wellAboveIndex: 0,
  };
  if (present(expectation)) {
    // Populate with fallback to 0 where not set
    indices = {
      belowIndex: Math.max(0, expectation.get('belowIndex') ?? 0),
      atIndex: Math.max(0, expectation.get('atIndex') ?? 0),
      aboveIndex: Math.max(0, expectation.get('aboveIndex') ?? 0),
      wellAboveIndex: Math.max(0, expectation.get('wellAboveIndex') ?? 0),
    };
  }

  result.expectation = {
    value: milestone.id,
    label: milestone.get('label'),
    data: {
      // Don't include "Well below" in the chart; absorb it into "Below".
      Below: indices.atIndex === 0 ? 0 : indices.atIndex + 0.5,
      At: indices.aboveIndex + 0.5,
      Above: indices.wellAboveIndex + 0.5,
      'Well above': levels.length - 1 + 0.5,
    },
  };

  if (present(progression)) {
    result.progress = {
      tick: milestone.id,
      label: `${milestone.label}:<br>${progression.get('level.label') as string}`,
      // A miss passes undefined, which indexOf reports as -1.
      value: levels.indexOf(
        findBy(levels, 'id', progression.get('level.id')) as Level,
      ),
    };
  }

  return result;
}

/**
 * generateChartDatapoint generates the required data point for charting
 * expectation data based on the provided expectations and milestones.
 * This is the legacy 4-area version.
 *
 * @param {Expectation} expectation - The expectation to generate a chart
 *     datapoint for.
 * @param {Milestone} milestone - The milestone to generate a chart datapoint
 *     for.
 * @param {Level[]} levels - The levels to generate a chart datapoint for.
 * @param {boolean} showAllColours - whether to include "well below" and
 *     "well above" colours.
 * @returns {ProgressionDataPoint}
 */
export function generateChartDatapoint(
  expectation: Expectation | undefined,
  milestone: Milestone,
  levels: readonly Level[],
  showAllColours: boolean,
): ProgressionDataPoint {
  // Defaults in case there is no expectation for this Milestone
  let expectationIndices = {
    belowIndex: 0,
    atIndex: 0,
    aboveIndex: 0,
    wellAboveIndex: 0,
  };

  if (present(expectation)) {
    // Populate with fallback to 0 where not set
    expectationIndices = {
      belowIndex: Math.max(0, expectation.get('belowIndex') ?? 0),
      atIndex: Math.max(0, expectation.get('atIndex') ?? 0),
      aboveIndex: Math.max(0, expectation.get('aboveIndex') ?? 0),
      wellAboveIndex: Math.max(0, expectation.get('wellAboveIndex') ?? 0),
    };
  }
  const nudge = function (val: number): number {
    if (val === 0) {
      return val;
    }
    return val + 0.5;
  };

  // By default, don't include "Well below" in the chart; absorb it into
  // "Below".
  let data: ExpectationIndicesData = {
    Below: nudge(expectationIndices.atIndex),
    At: nudge(expectationIndices.aboveIndex),
    Above: nudge(expectationIndices.wellAboveIndex),
  };
  if (showAllColours) {
    // The keys need to be in order, so we can't just inject the extras.
    data = {
      'Well below': nudge(expectationIndices.belowIndex),
      Below: nudge(expectationIndices.atIndex),
      At: nudge(expectationIndices.aboveIndex),
      Above: nudge(expectationIndices.wellAboveIndex),
      'Well above': levels.length,
    };
  }
  return {
    value: milestone.id,
    label: milestone.get('label'),
    data,
  };
}

/**
 * generateProgressMarker generates an expectation data point with values
 * that align with API specifications.
 *
 * @param {Expectation} expectation - The expectation to generate a raw
 *     datapoint for.
 * @param {Milestone} milestone - The milestone to generate a raw datapoint for.
 * @returns {ProgressionDataPoint}
 */
export function generateRawDatapoint(
  expectation: Expectation | undefined,
  milestone: Milestone,
): ProgressionDataPoint {
  // Defaults in case there is no expectation for this Milestone
  let expectationIndices = {
    belowIndex: 0,
    atIndex: 0,
    aboveIndex: 0,
    wellAboveIndex: 0,
  };

  if (present(expectation)) {
    // Populate with fallback to 0 where not set
    expectationIndices = {
      belowIndex: expectation.get('belowIndex') ?? 0,
      atIndex: expectation.get('atIndex') ?? 0,
      aboveIndex: expectation.get('aboveIndex') ?? 0,
      wellAboveIndex: expectation.get('wellAboveIndex') ?? 0,
    };
  }

  return {
    value: milestone.id,
    label: milestone.get('label'),
    data: {
      Below: expectationIndices.belowIndex,
      At: expectationIndices.atIndex,
      Above: expectationIndices.aboveIndex,
      'Well above': expectationIndices.wellAboveIndex,
    },
  };
}

/**
 * _newProgressionData returns a new ProgressionData container.
 *
 * @private
 * @returns {ProgressionData}
 */
function _newProgressionData(): ProgressionObject<ProgressionData> {
  // Explicit type arguments: create's inferred form only accepts keys
  // EmberObject already declares.
  return EmberObject.create<
    typeof EmberObject,
    ProgressionObject<ProgressionData>,
    keyof ProgressionData,
    [ProgressionData]
  >({
    // raw provides the raw expectation indices data.
    raw: [],
    // chart provides the chartable expectation indices data.
    chart: [],
    // progress maps the students progress to the milestones and expectations
    // in order to be able to generate chartable progress lines.
    progress: [],
  });
}

/**
 * generateExpectationData solely generates expectation data.
 * useful when charts are the same for a given subject, then progression data
 * can be generated independently for each student.
 *
 * @param {Expectation[]} expectations - The school's expectations.
 * @param {Milestone[]} milestones - The milestones to generate chart data for.
 * @param {Level[]} levels - The levels to generate chart data for.
 * @param {boolean} showAllColours - Whether to include all 5 progress colours.
 * @returns {ProgressionData}
 */
export function generateExpectationData(
  expectations: Iterable<Expectation>,
  milestones: readonly Milestone[],
  levels: readonly Level[],
  showAllColours: boolean,
): ProgressionObject<ProgressionData> {
  const data = _newProgressionData();
  milestones.forEach((milestone) => {
    _pushExpectationData(expectations, milestone, levels, data, showAllColours);
  });

  return data;
}

/**
 * generateProgressMarker returns a generated progress marker, or null.
 *
 * @param {Milestone} milestone - The milestone to generate a progress marker
 *     for.
 * @param {Level[]} levels - The levels to generate a progress marker for.
 * @param {Progression[]} progressions - The student's progressions to generate
 *     a progress marker for.
 * @returns {ProgressMarker|null}
 */
export function generateProgressMarker(
  milestone: Milestone,
  levels: readonly Level[],
  progressions: Iterable<Progression>,
): ProgressMarker | null {
  const progression = findBy(progressions, 'milestone.id', milestone.id);
  if (present(progression)) {
    return {
      tick: milestone.id,
      label: progression.get('level.label') as string,
      // A miss passes undefined, which indexOf reports as -1.
      value: levels.indexOf(
        findBy(levels, 'id', progression.get('level.id')) as Level,
      ),
    };
  }

  return null;
}

/**
 * _pushProgressMarker pushes a generated progress marker into an array of
 * progress.
 *
 * @private
 * @param {Milestone} milestone - The milestone to generate a progress marker
 *     for.
 * @param {Level[]} levels - The levels to generate a progress marker for.
 * @param {Progression[]} progressions - The student's progressions to generate
 *     a progress marker for.
 * @param {ProgressMarker[]} progress - The array to push generated progress
 *     markers into.
 */
function _pushProgressMarker(
  milestone: Milestone,
  levels: readonly Level[],
  progressions: Iterable<Progression>,
  progress: ProgressMarker[],
): void {
  const marker = generateProgressMarker(milestone, levels, progressions);
  if (marker !== null) {
    progress.push(marker);
  }
}

/**
 * generateProgressData returns progress data for a student.
 *
 * @param {Milestone[]} milestones - The milestones to generate progress data
 *     for.
 * @param {Level[]} levels - The levels to generate progress data for.
 * @param {Progression[]} progressions - The student's progressions to generate
 *     progress for.
 * @returns {ProgressMarker[]}
 */
export function generateProgressData(
  milestones: readonly Milestone[],
  levels: readonly Level[],
  progressions: Iterable<Progression>,
): ProgressMarker[] {
  // progress maps the students progress to the milestones and expectations
  // in order to be able to generate chartable progress lines.
  const progress: ProgressMarker[] = [];
  milestones.forEach((milestone) => {
    _pushProgressMarker(milestone, levels, progressions, progress);
  });

  return progress;
}

/**
 * _pushExpectationData provides a procedure to push generated expectation data
 * into the provided data object.
 *
 * @private
 * @param {Expectation[]} expectations - The school's expectations.
 * @param {Milestone} milestone - The milestone to generate chart data for.
 * @param {Level[]} levels - The levels to generate chart data for.
 * @param {ProgressionData} data - The data object to push chart data into.
 * @param {boolean} showAllColours - Whether to include all 5 progress colours.
 */
function _pushExpectationData(
  expectations: Iterable<Expectation>,
  milestone: Milestone,
  levels: readonly Level[],
  data: ProgressionData,
  showAllColours: boolean,
): void {
  const expectation = findBy(expectations, 'milestone.id', milestone.id);
  data.chart.push(
    generateChartDatapoint(expectation, milestone, levels, showAllColours),
  );
  data.raw.push(generateRawDatapoint(expectation, milestone));
}

/**
 * generateStudentProgressData returns expectation and progress data for a
 * student.
 *
 * @param {Expectation[]} expectations - The school's expectations.
 * @param {Milestone[]} milestones - Milestones sorted by 'sequence', 'label'.
 * @param {Level[]} levels - Levels sorted by 'sequence', 'label'.
 * @param {Progression[]} progressions - A Student's progress.
 * @param {boolean} showAllColours - Whether to include all 5 progress colours.
 * @returns {ProgressionData}
 */
// Update other functions to accept the flag
export function generateStudentProgressData(
  expectations: Iterable<Expectation>,
  milestones: readonly Milestone[],
  levels: readonly Level[],
  progressions: Iterable<Progression>,
  showAllColours: boolean,
): ProgressionObject<ProgressionData> {
  const data = _newProgressionData();
  milestones.forEach((milestone) => {
    _pushExpectationData(expectations, milestone, levels, data, showAllColours);
    _pushProgressMarker(milestone, levels, progressions, data.progress);
  });

  return data;
}
